/* nns-enhance.js — No Nonsense Shop storefront enhancements
 * Vanilla JS, no deps, no build step. Referenced from snippets/nns-global.liquid.
 * Features: (1) wishlist hearts on product cards, (2) wishlist drawer,
 *           (3) per-card trust strip, (4) account-page collected coupons.
 * Idempotent + MutationObserver-driven so it survives Horizon grid re-renders
 * (facets.js / results-list.js morph #ResultsList on every facet/sort/scroll).
 * Card/grid selectors per the reverse-engineering findings:
 *   card root  = product-card.product-card  (custom element, carries data-product-id)
 *   link       = a.product-card__link[href] (handle derivable; no handle attr emitted)
 *   media wrap = .product-media-container    (positioned; badge overlay lives here)
 *   price      = .price                       (block container; trust strip goes after)
 *   title      = .product-card__title         (visible title; ignore zoom-out dupe)
 */
(function () {
  "use strict";

  // Guard against double-execution (asset referenced twice / theme-editor reloads).
  if (window.__nnsEnhanceLoaded) return;
  window.__nnsEnhanceLoaded = true;

  var WISH_KEY = "nns_wishlist";
  var EARN_PREFIX = /^nns_earn_/;

  /* ---------- NNS MyHub backend sync (graceful: localStorage stays the fallback) ---------- */
  var HUB_API = "/apps/nns-hub";
  var HUB_FIELD = { nns_profile: "profile", nns_interests: "interests", nns_coupons: "coupons", nns_wishlist: "wishlist" };
  var HUB_KEY = { profile: "nns_profile", interests: "nns_interests", coupons: "nns_coupons", wishlist: "nns_wishlist" };
  window.NNS_HUB = window.NNS_HUB || { live: false };
  var _setItem;
  try { _setItem = window.localStorage.setItem.bind(window.localStorage); } catch (e) { _setItem = function () {}; }
  // mirror writes of the 4 hub keys to the customer's Shopify account when the backend is live
  try {
    window.localStorage.setItem = function (k, val) {
      _setItem(k, val);
      if (/^nns_earn_/.test(k)) { try { reportEarnWrite(k, val); } catch (e) {} }
      if (window.NNS_HUB.live && HUB_FIELD[k]) {
        try {
          var field = HUB_FIELD[k], b = {};
          try { b[field] = JSON.parse(val); } catch (e) { b[field] = val; }
          fetch(HUB_API + "/" + field, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(b), keepalive: true }).catch(function () {});
        } catch (e) {}
      }
    };
  } catch (e) {}
  // pull account data on load; hydrate localStorage + signal the hub to re-render
  window.NNS_HUB.pull = function () {
    fetch(HUB_API + "/hub", { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || d.anon || !d.data) return;        // logged out / not deployed -> stay on localStorage
        window.NNS_HUB.live = true;
        var X = d.data;
        Object.keys(HUB_KEY).forEach(function (f) {
          if (X[f] !== undefined && X[f] !== null) { try { _setItem(HUB_KEY[f], JSON.stringify(X[f])); } catch (e) {} }
        });
        try { if (typeof refreshWishCount === "function") refreshWishCount(); } catch (e) {}
        try { window.dispatchEvent(new CustomEvent("nns-hub-synced")); } catch (e) {}
      })
      .catch(function () {});
  };
  // report a game result (called from the games page) -> leaderboard + server-granted coupon
  window.NNS_HUB.gameResult = function (game, score, coupon, name) {
    if (!window.NNS_HUB.live) return Promise.resolve(null);
    return fetch(HUB_API + "/game-result", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ game: game, score: score, coupon: coupon, name: name }) })
      .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  };
  // observe game wins (zero-touch to the games file): on each nns_earn_* write, report newly-won games to the leaderboard
  var EARN_SEEN = {};
  function snapshotEarn() {
    try { for (var i = 0; i < localStorage.length; i++) { var k = localStorage.key(i); if (k && /^nns_earn_/.test(k)) { EARN_SEEN[k] = EARN_SEEN[k] || {}; try { var s = JSON.parse(localStorage.getItem(k)); (s && s.won || []).forEach(function (id) { EARN_SEEN[k][id] = 1; }); } catch (e) {} } } } catch (e) {}
  }
  function reportEarnWrite(k, val) {
    if (!window.NNS_HUB.live) return;
    var s; try { s = JSON.parse(val); } catch (e) { return; }
    if (!s || !Array.isArray(s.won)) return;
    var seen = EARN_SEEN[k] || (EARN_SEEN[k] = {});
    var name = 'Player';
    try { var p = JSON.parse(localStorage.getItem('nns_profile') || '{}'); if (p && (p.name || p.firstname)) name = String(p.name || p.firstname).slice(0, 24); } catch (e) {}
    s.won.forEach(function (id) {
      if (seen[id]) return;
      seen[id] = 1;
      var att = (s.attempts && s.attempts[id]) || 1;
      var score = Math.max(1, 1000 - (att - 1) * 100);
      try { window.NNS_HUB.gameResult(id, score, null, name); } catch (e) {}
    });
  }
  try { snapshotEarn(); } catch (e) {}
  try { window.NNS_HUB.pull(); } catch (e) {}

  /* ---------- tiny helpers ---------- */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  // Safe localStorage read of a JSON array; fail closed (return []).
  function readJSON(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return fallback;
      var v = JSON.parse(raw);
      return v == null ? fallback : v;
    } catch (e) { return fallback; }
  }
  function writeJSON(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); return true; }
    catch (e) { return false; }
  }
  // Minimal HTML-escape for any user/product-derived text we inject as markup.
  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  var HEART_SVG =
    '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
    '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';

  /* ---------- wishlist store ---------- */

  function getWishlist() {
    var arr = readJSON(WISH_KEY, []);
    return Array.isArray(arr) ? arr : [];
  }
  function wishKeyOf(item) { return item && (item.id != null ? "id:" + item.id : "url:" + item.url); }
  function isSaved(item) {
    var list = getWishlist(), k = wishKeyOf(item);
    for (var i = 0; i < list.length; i++) { if (wishKeyOf(list[i]) === k) return true; }
    return false;
  }
  // Toggle membership; returns the new saved-state boolean.
  function toggleWishlist(item) {
    var list = getWishlist(), k = wishKeyOf(item), out = [], found = false;
    for (var i = 0; i < list.length; i++) {
      if (wishKeyOf(list[i]) === k) { found = true; } else { out.push(list[i]); }
    }
    if (!found) out.push(item);
    writeJSON(WISH_KEY, out);
    refreshWishCount();
    return !found;
  }
  function removeFromWishlist(key) {
    var list = getWishlist(), out = [];
    for (var i = 0; i < list.length; i++) { if (wishKeyOf(list[i]) !== key) out.push(list[i]); }
    writeJSON(WISH_KEY, out);
    refreshWishCount();
    syncHeartStates();
  }

  /* ---------- read product info off a card ---------- */

  function cardData(card) {
    var id = card.getAttribute("data-product-id") || "";
    var link = $("a.product-card__link", card) || $("a[href*='/products/']", card);
    var url = link ? link.getAttribute("href") || "" : "";
    var titleNode = $(".product-card__title", card) ||
      $("a[href*='/products/'] .visually-hidden", card) ||
      $(".product-card__link .visually-hidden", card);
    var title = titleNode ? (titleNode.textContent || "").trim() : "";
    // Price: stable container, ignore PDP/cart .price elsewhere by scoping to card.
    var priceNode = $(".price", card);
    var price = priceNode ? (priceNode.textContent || "").replace(/\s+/g, " ").trim() : "";
    var imgNode = $(".product-media-container img", card) || $("img", card);
    var img = "";
    if (imgNode) {
      img = imgNode.getAttribute("src") || "";
      if (!img) {
        var ss = imgNode.getAttribute("srcset") || "";
        if (ss) img = ss.split(",")[0].trim().split(" ")[0];
      }
    }
    if (img && img.indexOf("//") === 0) img = "https:" + img;
    return { id: id, url: url, title: title, price: price, img: img };
  }

  /* ---------- (1) WISHLIST HEART injection ---------- */

  function injectHeart(card) {
    if (!card || card.getAttribute("data-nns-wish") === "1") return; // idempotent guard
    var mediaWrap = $(".product-media-container", card) || $(".product-media", card);
    if (!mediaWrap) return; // degrade gracefully if leaf markup differs
    card.setAttribute("data-nns-wish", "1");

    var btn = el("button", "nns-wish");
    btn.type = "button";
    btn.setAttribute("aria-label", "Save to wishlist");
    btn.innerHTML = HEART_SVG;

    var data = cardData(card);
    if (isSaved(data)) btn.classList.add("is-saved");

    btn.addEventListener("click", function (ev) {
      // Card has a full-overlay anchor (a.product-card__link); stop the click
      // falling through to the product link.
      ev.preventDefault();
      ev.stopPropagation();
      var fresh = cardData(card); // re-read in case the card re-rendered
      var nowSaved = toggleWishlist(fresh);
      btn.classList.toggle("is-saved", nowSaved);
      btn.setAttribute("aria-pressed", nowSaved ? "true" : "false");
    });

    mediaWrap.appendChild(btn);
  }

  // Keep every visible heart's active state in sync with the store.
  function syncHeartStates() {
    $all("product-card.product-card[data-nns-wish='1']").forEach(function (card) {
      var btn = $(".nns-wish", card);
      if (!btn) return;
      var saved = isSaved(cardData(card));
      btn.classList.toggle("is-saved", saved);
      btn.setAttribute("aria-pressed", saved ? "true" : "false");
    });
  }

  /* ---------- (3) TRUST STRIP — REMOVED ----------
     The per-card "UK stock · 1yr warranty · tracked" row was disabled (user
     found it cluttered). The dead injectTrust() function and its per-card call
     have been removed; the matching CSS in nns-cards.css §5b is gone too. */

  /* ---------- per-card enhancement pass (idempotent) ---------- */

  function enhanceCards(root) {
    var cards = $all("product-card.product-card", root || document);
    for (var i = 0; i < cards.length; i++) {
      injectHeart(cards[i]);
    }
  }

  /* ---------- (2) WISHLIST COUNT INDICATOR + DRAWER ---------- */

  function findCartLink() {
    // Mirror nns-global's cart discovery.
    return $('a[href="/cart"]') || $('a[href^="/cart"]') ||
      $('[class*="header"] a[href*="/cart"]') || $('a[href*="/cart"]');
  }

  function refreshWishCount() {
    var n = getWishlist().length;
    var btn = document.getElementById("nns-wish-trigger");
    if (!btn) return;
    var bubble = $(".nns-wish-count", btn);
    if (bubble) {
      bubble.textContent = n;
      bubble.style.display = n > 0 ? "" : "none";
    }
  }

  function injectWishTrigger() {
    if (document.getElementById("nns-wish-trigger")) return;
    var cart = findCartLink();
    if (!cart || !cart.parentNode) return;
    var btn = el("button", "nns-wish-trigger");
    btn.id = "nns-wish-trigger";
    btn.type = "button";
    btn.setAttribute("aria-label", "Open wishlist");
    btn.innerHTML = HEART_SVG + '<span class="nns-wish-count" aria-hidden="true">0</span>';
    btn.addEventListener("click", function (e) { e.preventDefault(); openDrawer(); });
    cart.parentNode.insertBefore(btn, cart); // place just before the cart icon
    refreshWishCount();
  }

  function buildDrawer() {
    if (document.getElementById("nns-wish-drawer")) return;
    var overlay = el("div", "nns-wish-overlay");
    overlay.id = "nns-wish-overlay";
    overlay.addEventListener("click", closeDrawer);

    var drawer = el("aside", "nns-wish-drawer");
    drawer.id = "nns-wish-drawer";
    drawer.setAttribute("role", "dialog");
    drawer.setAttribute("aria-label", "Your wishlist");
    drawer.setAttribute("aria-hidden", "true");
    drawer.innerHTML =
      '<div class="nns-wish-head">' +
        '<span class="nns-wish-title">Your wishlist</span>' +
        '<button type="button" class="nns-wish-close" aria-label="Close wishlist">&times;</button>' +
      '</div>' +
      '<div class="nns-wish-body" id="nns-wish-body"></div>';

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    $(".nns-wish-close", drawer).addEventListener("click", closeDrawer);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && drawer.classList.contains("is-open")) closeDrawer();
    });
  }

  function renderDrawerBody() {
    var body = document.getElementById("nns-wish-body");
    if (!body) return;
    var list = getWishlist();
    if (!list.length) {
      body.innerHTML =
        '<div class="nns-wish-empty">' +
          '<p class="nns-wish-empty-h">No saved items yet</p>' +
          '<p class="nns-wish-empty-p">Tap the heart on any product to save it here for later.</p>' +
          '<a class="nns-wish-cta" href="/collections/all">Browse products</a>' +
        '</div>';
      return;
    }
    var html = '<ul class="nns-wish-list">';
    for (var i = 0; i < list.length; i++) {
      var it = list[i], key = wishKeyOf(it);
      html +=
        '<li class="nns-wish-item" data-key="' + esc(key) + '">' +
          '<a class="nns-wish-thumb" href="' + esc(it.url || "#") + '">' +
            (it.img ? '<img src="' + esc(it.img) + '" alt="" loading="lazy">' : '<span class="nns-wish-noimg"></span>') +
          '</a>' +
          '<div class="nns-wish-meta">' +
            '<a class="nns-wish-name" href="' + esc(it.url || "#") + '">' + esc(it.title || "Product") + '</a>' +
            (it.price ? '<span class="nns-wish-price">' + esc(it.price) + '</span>' : '') +
            '<div class="nns-wish-actions">' +
              '<a class="nns-wish-view" href="' + esc(it.url || "#") + '">View</a>' +
              '<button type="button" class="nns-wish-remove" data-key="' + esc(key) + '" aria-label="Remove from wishlist">Remove</button>' +
            '</div>' +
          '</div>' +
        '</li>';
    }
    html += '</ul>';
    body.innerHTML = html;

    // Wire remove buttons (delegated would also work; explicit is clearer here).
    $all(".nns-wish-remove", body).forEach(function (b) {
      b.addEventListener("click", function () {
        removeFromWishlist(b.getAttribute("data-key"));
        renderDrawerBody();
      });
    });
  }

  function openDrawer() {
    buildDrawer();
    renderDrawerBody();
    var drawer = document.getElementById("nns-wish-drawer");
    var overlay = document.getElementById("nns-wish-overlay");
    if (overlay) overlay.classList.add("is-open");
    if (drawer) { drawer.classList.add("is-open"); drawer.setAttribute("aria-hidden", "false"); }
    document.documentElement.classList.add("nns-wish-locked");
  }

  function closeDrawer() {
    var drawer = document.getElementById("nns-wish-drawer");
    var overlay = document.getElementById("nns-wish-overlay");
    if (overlay) overlay.classList.remove("is-open");
    if (drawer) { drawer.classList.remove("is-open"); drawer.setAttribute("aria-hidden", "true"); }
    document.documentElement.classList.remove("nns-wish-locked");
  }

  /* ---------- (4) ACCOUNT COUPONS panel ---------- */

  function isAccountPage() {
    var p = location.pathname || "";
    if (p.indexOf("/account") === 0 || p.indexOf("/pages/my-account") === 0) return true;
    // account-hub section/body class per findings.
    if ($(".account-hub, [class*='account-hub'], section.account-hub")) return true;
    if (document.body && /account/i.test(document.body.className || "")) return true;
    return false;
  }

  // Pull every coupon-ish value out of one parsed nns_earn_* payload.
  // Shapes vary: {won:[...], claimed:bool}, {coupons:[...]}, {code:"..."}, ["CODE"], "CODE".
  function extractCoupons(parsed) {
    var out = [];
    function pushCode(v) {
      if (v == null) return;
      if (typeof v === "string") {
        var s = v.trim();
        if (s) out.push({ code: s });
      } else if (typeof v === "object") {
        var code = v.code || v.coupon || v.discount || v.prize || v.value || v.reward;
        if (code) out.push({ code: String(code), note: v.note || v.expires || v.valid || v.label || "" });
      }
    }
    try {
      if (parsed == null) return out;
      if (typeof parsed === "string") { pushCode(parsed); return out; }
      if (Array.isArray(parsed)) { parsed.forEach(pushCode); return out; }
      if (typeof parsed === "object") {
        ["won", "claimed", "coupons", "codes", "rewards", "prizes"].forEach(function (k) {
          var v = parsed[k];
          if (Array.isArray(v)) v.forEach(pushCode);
        });
        // Also handle a single coupon object at the top level.
        if (parsed.code || parsed.coupon || parsed.discount) pushCode(parsed);
      }
    } catch (e) { /* fail closed */ }
    return out;
  }

  function collectAllCoupons() {
    var all = [], seen = {};
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i);
        if (!key || !EARN_PREFIX.test(key)) continue;
        var parsed = readJSON(key, null);
        if (parsed == null) continue; // parse failed -> skip silently (fail closed)
        var coupons = extractCoupons(parsed);
        for (var j = 0; j < coupons.length; j++) {
          var c = coupons[j];
          var dedupe = (c.code || "").toUpperCase();
          if (!dedupe || seen[dedupe]) continue;
          seen[dedupe] = 1;
          all.push(c);
        }
      }
    } catch (e) { return []; } // never throw on the account page
    return all;
  }

  function renderCoupons() {
    if (!isAccountPage()) return;
    // Defer to the redesigned account hub — it renders its own coupons panel
    // (and the standing £10 credit) from the same nns_earn_* localStorage keys.
    if (document.querySelector("[data-nns-acct], .nns-acct")) return;
    if (document.getElementById("nns-coupons")) return; // idempotent

    var coupons;
    try { coupons = collectAllCoupons(); } catch (e) { return; } // fail closed: render nothing

    var host = $("main") || document.getElementById("MainContent") || document.body;
    if (!host) return;

    var panel = el("section", "nns-coupons");
    panel.id = "nns-coupons";

    if (!coupons.length) {
      panel.innerHTML =
        '<h2 class="nns-coupons-h">Your collected coupons</h2>' +
        '<div class="nns-coupons-empty">' +
          '<p>You have not collected any coupons yet.</p>' +
          '<a class="nns-coupons-cta" href="/pages/earn-credits">Play &amp; Earn to collect coupons</a>' +
        '</div>';
      host.insertBefore(panel, host.firstChild);
      return;
    }

    var html = '<h2 class="nns-coupons-h">Your collected coupons</h2><ul class="nns-coupons-list">';
    for (var i = 0; i < coupons.length; i++) {
      var c = coupons[i];
      html +=
        '<li class="nns-coupon">' +
          '<code class="nns-coupon-code">' + esc(c.code) + '</code>' +
          (c.note ? '<span class="nns-coupon-note">' + esc(c.note) + '</span>' : '<span class="nns-coupon-note">Apply at checkout while valid</span>') +
          '<button type="button" class="nns-coupon-copy" data-code="' + esc(c.code) + '">Copy</button>' +
        '</li>';
    }
    html += '</ul>';
    panel.innerHTML = html;
    host.insertBefore(panel, host.firstChild);

    // Copy buttons (Clipboard API w/ a graceful fallback).
    $all(".nns-coupon-copy", panel).forEach(function (b) {
      b.addEventListener("click", function () {
        var code = b.getAttribute("data-code") || "";
        function done() { var t = b.textContent; b.textContent = "Copied"; setTimeout(function () { b.textContent = t; }, 1400); }
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(done, function () { fallbackCopy(code); done(); });
          } else { fallbackCopy(code); done(); }
        } catch (e) { fallbackCopy(code); done(); }
      });
    });
  }

  function fallbackCopy(text) {
    try {
      var ta = el("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch (e) { /* no-op */ }
  }

  /* ---------- boot + observer ---------- */

  // Schedule low-priority work for browser idle time (graceful fallback).
  var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 120); };

  // Full first pass: scan the whole document once on boot.
  function runOnce() {
    try { enhanceCards(document); } catch (e) {}
    try { injectWishTrigger(); } catch (e) {}
    try { syncHeartStates(); } catch (e) {}
    // Coupons only ever render on the account page and require a full
    // localStorage walk — defer to idle so it never competes with first paint.
    idle(function () { try { renderCoupons(); } catch (e) {} });
  }

  // Incremental re-decoration after a grid morph. We enhance ONLY the subtrees
  // that actually changed (queued from the observer) instead of re-scanning the
  // entire document — Horizon morphs #ResultsList on every facet/sort/scroll, so
  // a document-wide querySelectorAll on each batch is the dominant main-thread
  // cost. injectHeart() already sets the correct saved-state on each new card, and
  // syncHeartStates() is called explicitly on every wishlist mutation, so it is
  // not needed here (states don't change across a pure re-render). The wishlist
  // trigger never disappears once placed, so we only re-inject it if missing.
  function runIncremental(roots) {
    var didDoc = false;
    for (var i = 0; i < roots.length; i++) {
      var r = roots[i];
      if (r === document) {
        // Coarse body-swap path: full-document rescan (matches the original
        // safety net). Only ever do it once per flush.
        if (didDoc) continue;
        didDoc = true;
        try { enhanceCards(document); } catch (e) {}
        continue;
      }
      if (!r || r.nodeType !== 1 || !r.isConnected) continue;
      try {
        // The mutated root may itself be a card, or contain cards.
        if (r.matches && r.matches("product-card.product-card")) injectHeart(r);
        else enhanceCards(r);
      } catch (e) {}
    }
    if (!document.getElementById("nns-wish-trigger")) {
      try { injectWishTrigger(); } catch (e) {}
    }
  }

  // Debounced re-run so facet/sort/scroll re-renders re-decorate new cards.
  // Coalesce bursts of mutations into ONE pass: schedule on the next frame, then a
  // short timeout window, so a flurry of added nodes (Horizon morphing the whole
  // #ResultsList) costs a single scoped enhance pass, not one per mutation.
  // (Previously a stray `60` was passed to requestAnimationFrame — which ignores
  // extra args — so there was effectively no debounce at all.)
  var pending = false;
  var dirtyRoots = [];        // element subtrees added since the last flush
  // Queue the real (foreign) element subtrees from a batch; returns how many were
  // queued so the caller can skip scheduling work when only our OWN injected nodes
  // (hearts/triggers/drawers) changed — otherwise the script would re-trigger itself.
  function queueRoots(muts) {
    var added0 = dirtyRoots.length;
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      if (!added) continue;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n && n.nodeType === 1 && !isOurNode(n)) dirtyRoots.push(n);
      }
    }
    return dirtyRoots.length - added0;
  }
  function scheduleRun() {
    if (pending) return;
    pending = true;
    var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    raf(function () {
      setTimeout(function () {
        pending = false;
        var roots = dirtyRoots;
        dirtyRoots = [];
        // Nothing real was added (only our own injected nodes were filtered out)
        // → no work to do. The boot pass already decorated the existing document.
        if (roots.length) runIncremental(roots);
      }, 60);
    });
  }

  // True only when EVERY added node in a record is something we injected
  // ourselves (drawer, overlay, coupons, hearts, wish trigger). Lets us ignore
  // our own DOM churn so it never triggers a full-document re-scan.
  function isOurNode(n) {
    if (!n || n.nodeType !== 1) return true; // text/comment nodes: ignore
    var id = n.id || "";
    if (id.indexOf("nns-") === 0) return true;
    var cls = (typeof n.className === "string") ? n.className : "";
    return /\bnns-(wish|coupon)/.test(cls);
  }
  function onlyOurMutations(muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      if (!added || !added.length) continue;
      for (var j = 0; j < added.length; j++) {
        if (!isOurNode(added[j])) return false; // a real (foreign) node appeared
      }
    }
    return true;
  }

  function startObserver() {
    if (!("MutationObserver" in window)) return;
    var target = document.getElementById("ResultsList") ||
      $("results-list") || $("main") || document.body;
    if (!target) return;
    var obs = new MutationObserver(function (muts) {
      // queueRoots already filters out our own injected nodes; only schedule a
      // flush when a real (foreign) subtree was added (e.g. a Horizon grid morph).
      if (queueRoots(muts) > 0) scheduleRun();
    });
    obs.observe(target, { childList: true, subtree: true });
    // Defensive: also watch body in case the whole section is swapped. Skip the
    // whole batch fast when only our OWN injected elements changed (drawers/
    // overlays/coupons appending to <body> on wishlist open/close) — otherwise
    // every open/close would queue a re-scan and layout thrash. A real body-level
    // swap is rare (not per-scroll), and the added node may be a wrapper whose
    // cards live deep inside it, so for THIS coarse path we queue the whole
    // document — matching the original full-document safety net — while the hot
    // ResultsList path above stays scoped to the exact mutated subtrees.
    if (target !== document.body && document.body) {
      new MutationObserver(function (muts) {
        if (onlyOurMutations(muts)) return;
        // Confirm a real foreign node was added before committing to a full scan.
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          if (!added) continue;
          for (var j = 0; j < added.length; j++) {
            if (!isOurNode(added[j])) { dirtyRoots.push(document); scheduleRun(); return; }
          }
        }
      }).observe(document.body, { childList: true });
    }
  }

  function boot() {
    runOnce();
    startObserver();
    // Header can re-render (sticky/transparent) — retry the trigger briefly,
    // matching the nns-global pattern. Skip the timer entirely if runOnce()
    // already placed the trigger above.
    if (!document.getElementById("nns-wish-trigger")) {
      var n = 0, iv = setInterval(function () {
        try { injectWishTrigger(); } catch (e) {}
        if (++n > 10 || document.getElementById("nns-wish-trigger")) clearInterval(iv);
      }, 400);
    }
  }

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);
})();

/* == NNS product page: wide description + gallery arrows (desktop, 2026-08-12) == */
(function(){
  function nnsProductTweaks(){
    var grid = document.querySelector('.product-information__grid');
    if (!grid || !window.matchMedia('(min-width: 990px)').matches) return;
    /* 2) prev/next arrows under the gallery */
    if (!document.querySelector('.nns-gal-nav')) {
      var media = document.querySelector('.product-information__media');
      var sc = media && media.querySelector('slideshow-component');
      if (media && sc && typeof sc.next === 'function') {
        var nav = document.createElement('div');
        nav.className = 'nns-gal-nav';
        nav.innerHTML = '<button type="button" class="nns-gal-btn" data-dir="prev" aria-label="Previous image">&#8592;</button>' +
                        '<button type="button" class="nns-gal-btn" data-dir="next" aria-label="Next image">&#8594;</button>';
        var slwrap = sc.closest('slideshow-container') || sc;
        slwrap.parentNode.insertBefore(nav, slwrap.nextSibling);
        nav.addEventListener('click', function(e){
          var b = e.target.closest('.nns-gal-btn');
          if (!b) return;
          if (b.getAttribute('data-dir') === 'next') { sc.next(); } else { sc.previous(); }
        });
      }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', nnsProductTweaks);
  else nnsProductTweaks();
  document.addEventListener('shopify:section:load', nnsProductTweaks);
})();
